import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Send, Plus, Loader2, RefreshCw,
         Search, ShieldCheck, CheckCircle2, XCircle, Trophy,
         Zap, Download } from "lucide-react";
import { Button } from "./ui/button";
import { AdminPayoutsDesk } from "./AdminPayoutsDesk";
import { AdminWalletRestrictions } from "./AdminWalletRestrictions";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "./ui/dialog";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

export function AdminWalletPanel({ api }) {
  const [tab, setTab] = useState("liquidity");
  const [payoutCount, setPayoutCount] = useState(0);
  const [inductionCount, setInductionCount] = useState(0);
  const [inductionNewToday, setInductionNewToday] = useState(0);

  // Live count of pending payouts (used by the tab badge)
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/payouts?status=pending");
        setPayoutCount((data.counts && data.counts.pending) || (data.count || 0));
      } catch { /* silent */ }
      try {
        const { data } = await api.get("/admin/wallet/induction/pending");
        setInductionCount((data.counts && data.counts.pending) || (data.count || 0));
        setInductionNewToday((data.counts && data.counts.new_today) || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [api]);

  const TABS = [
    { key: "liquidity", label: "Global Liquidity" },
    { key: "wallets",   label: "All Wallets" },
    { key: "invites",   label: "Wallet Invites" },
    { key: "induction", label: "Induction Pending", badge: inductionCount, pulse: inductionNewToday > 0 },
    { key: "payouts",   label: "Payouts", badge: payoutCount },
    { key: "refunds",   label: "Refund Requests" },
    { key: "restrictions", label: "Payment Restrictions" },
    { key: "credit",    label: "Credit User" },
  ];

  return (
    <div data-testid="admin-wallet-panel" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#B87333] inline-flex items-center gap-1.5">
            <Wallet size={11} /> Vinci Payout Engine · Admin Console
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2">Global Wallet Console</h2>
          <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
            Ecosystem-wide liquidity, per-user balances, pending
            withdrawals, refund requests, and the Direct-Credit push
            tool for bonuses, commissions and reconciliation.
          </p>
        </div>
      </div>

      <div className="border-b border-white/10 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`awp-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] whitespace-nowrap border-b-2 transition-all inline-flex items-center gap-2 ${
                    tab === t.key
                      ? "border-[#D8B23E] text-[#D8B23E]"
                      : "border-transparent text-[#8A8A82] hover:text-[#C9C9C2]"
                  }`}>
            {t.pulse && (
              <span aria-label="new-today" data-testid={`awp-tab-${t.key}-pulse`}
                     className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#C0392B] opacity-70 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C0392B]" />
              </span>
            )}
            {t.label}
            {t.badge > 0 && (
              <span className="bg-[#C0392B] text-white text-[9px] rounded-full px-1.5 py-0.5 font-mono">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "liquidity" && <LiquidityView api={api} />}
      {tab === "wallets"   && <WalletsView   api={api} />}
      {tab === "invites"   && <InvitesView   api={api} />}
      {tab === "induction" && <InductionView api={api} onCountChange={setInductionCount} onNewTodayChange={setInductionNewToday} />}
      {tab === "payouts"   && <AdminPayoutsDesk api={api} onCountChange={setPayoutCount} embedded />}
      {tab === "restrictions" && <AdminWalletRestrictions api={api} />}
      {tab === "refunds"   && <RefundsView   api={api} />}
      {tab === "credit"    && <CreditUserView api={api} />}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   InvitesView · every Vinci Wallet activation link with per-row
   Resend + Regenerate actions. TTL is 48 hours per board policy.
   ═════════════════════════════════════════════════════════════════ */
function InvitesView({ api }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busyToken, setBusyToken] = useState("");
  const [ttlHours, setTtlHours] = useState(48);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wallet/invites?status=${status}`);
      setRows(data.rows || []);
      if (data.ttl_hours) setTtlHours(data.ttl_hours);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load invites");
    } finally { setLoading(false); }
  }, [api, status]);
  useEffect(() => { load(); }, [load]);

  const resend = async (r) => {
    setBusyToken(r.token);
    try {
      await api.post(`/admin/wallet/invites/${r.token}/resend`, {});
      toast.success(`Invite re-sent to ${r.user_email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend invite");
    } finally { setBusyToken(""); }
  };
  const regenerate = async (r) => {
    if (!window.confirm(
      `Regenerate a fresh 48-hour activation link for ${r.user_email}?\n\n`
      + `The previous link will be invalidated instantly.`)) return;
    setBusyToken(r.token);
    try {
      const { data } = await api.post(`/admin/wallet/invites/${r.token}/regenerate`, {});
      toast.success(`Fresh link minted for ${r.user_email}`, {
        description: `Expires: ${new Date(data.expires_at).toLocaleString()}`,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate invite");
    } finally { setBusyToken(""); }
  };
  const copyLink = (r) => {
    const url = `${window.location.origin}/wallet/activate/${r.token}`;
    navigator.clipboard.writeText(url);
    toast.info("Activation link copied");
  };

  const uiTone = (s) => s === "used" ? "#2FB999"
                        : s === "live" ? "#D8B23E"
                        : s === "expired" ? "#C0392B"
                        : "#8A8A82";

  return (
    <div className="space-y-4" data-testid="awp-invites-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-xl text-[#F5F5F0] flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#D8B23E]" /> Wallet Invitations
          </h3>
          <p className="text-sm text-[#8A8A82] mt-1 max-w-2xl">
            Every activation link ever minted, newest first. Links stay
            live for <b className="text-[#D8B23E]">{ttlHours} hours</b> per
            board policy. Use <b className="text-[#F5F5F0]">Resend</b> to
            re-deliver a live link or <b className="text-[#F5F5F0]">Regenerate</b> to
            issue a fresh one and invalidate the old.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", "sent", "used", "expired", "regenerated"].map(s => (
            <button key={s}
                     data-testid={`awp-invites-filter-${s}`}
                     onClick={() => setStatus(s)}
                     className={`px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] ${status === s ? "bg-[#F5F5F0] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}>
              {s}
            </button>
          ))}
          <Button data-testid="awp-invites-refresh" onClick={load}
                   variant="outline" className="border-white/20 text-[#C9C9C2]">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></div>
      ) : rows.length === 0 ? (
        <div className="py-14 text-center text-[#8A8A82] font-body text-sm border border-dashed border-white/10">
          No invites in this lane.
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-[13px]" data-testid="awp-invites-table">
            <thead className="bg-white/[0.03] text-[#8A8A82]">
              <tr>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Applicant</th>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Status</th>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Sent</th>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Expires</th>
                <th className="text-center px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Sends</th>
                <th className="text-right px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const alive = r.ui_status === "live";
                const gone = r.ui_status === "used";
                const isBusy = busyToken === r.token;
                return (
                  <tr key={r.token} data-testid={`awp-invite-${r.token}`}
                      className="border-t border-white/5 align-top">
                    <td className="px-4 py-3">
                      <div className="text-[#F5F5F0] font-body">{r.name || "—"}</div>
                      <div className="text-[#8A8A82] text-xs">{r.user_email}</div>
                      <div className="text-[10px] text-[#6f6f68] font-mono mt-1">
                        {r.token.slice(0, 12)}…
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em]"
                            style={{ color: uiTone(r.ui_status) }}>
                        {r.ui_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#C9C9C2]">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#C9C9C2]">
                      {r.expires_at ? new Date(r.expires_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-[#D8B23E]">
                      {r.send_count || 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="inline-flex gap-1.5 justify-end w-full">
                        <button type="button"
                                 data-testid={`awp-invite-copy-${r.token}`}
                                 onClick={() => copyLink(r)}
                                 title="Copy activation link"
                                 className="inline-flex items-center gap-1 px-2 py-1 text-[9px] font-grotesk uppercase tracking-[0.18em] border border-white/15 text-[#C9C9C2] hover:border-[#B87333] hover:text-[#B87333] rounded-sm">
                          Copy
                        </button>
                        <button type="button"
                                 data-testid={`awp-invite-resend-${r.token}`}
                                 onClick={() => resend(r)}
                                 disabled={!alive || isBusy}
                                 title={alive
                                    ? "Re-email the same activation link"
                                    : gone
                                      ? "Wallet already active"
                                      : "Link expired — use Regenerate instead"}
                                 className="inline-flex items-center gap-1 px-2 py-1 text-[9px] font-grotesk uppercase tracking-[0.18em] border border-[#D8B23E]/40 hover:border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
                          {isBusy ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                          Resend
                        </button>
                        <button type="button"
                                 data-testid={`awp-invite-regenerate-${r.token}`}
                                 onClick={() => regenerate(r)}
                                 disabled={gone || isBusy}
                                 title={gone
                                    ? "Wallet already active — nothing to regenerate"
                                    : "Mint a fresh 48-hour link and invalidate the old one"}
                                 className="inline-flex items-center gap-1 px-2 py-1 text-[9px] font-grotesk uppercase tracking-[0.18em] border border-[#B87333]/60 text-[#F6E4A6] hover:bg-[#B87333] hover:text-black rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
                          {isBusy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                          Regenerate
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════ */
function InductionView({ api, onCountChange, onNewTodayChange }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [authorizeFor, setAuthorizeFor] = useState(null);
  const [conditions, setConditions] = useState("");
  const [newToday, setNewToday] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wallet/induction/pending?status=${status}`);
      setRows(data.rows || []);
      const c = data.counts || {};
      if (onCountChange) onCountChange(c.pending || 0);
      const nt = c.new_today || 0;
      setNewToday(nt);
      if (onNewTodayChange) onNewTodayChange(nt);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load induction queue");
    } finally { setLoading(false); }
  }, [api, status, onCountChange, onNewTodayChange]);

  useEffect(() => { load(); }, [load]);
  // Gentle 60s polling so a newly-submitted application appears
  // without the admin having to refresh the page manually.
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const authorize = (r) => { setAuthorizeFor(r); setConditions(""); };
  const submitAuthorize = async () => {
    if (!authorizeFor) return;
    setBusyId(authorizeFor.id);
    try {
      await api.post(`/admin/wallet/induction/${authorizeFor.id}/authorize`,
                       { executive_conditions: conditions.trim() });
      toast.success(`✦ Wallet authorised for ${authorizeFor.user_email}${conditions.trim() ? " · conditions attached" : ""}`);
      setAuthorizeFor(null); setConditions("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Authorization failed");
    } finally { setBusyId(""); }
  };
  const submitReject = async () => {
    if (!rejectFor) return;
    if ((rejectReason || "").trim().length < 6) {
      toast.error("Rejection reason must be at least 6 characters"); return;
    }
    setBusyId(rejectFor.id);
    try {
      await api.post(`/admin/wallet/induction/${rejectFor.id}/reject`,
                       { reason: rejectReason.trim() });
      toast.info(`Application rejected · ${rejectFor.user_email} notified`);
      setRejectFor(null); setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rejection failed");
    } finally { setBusyId(""); }
  };
  const openMandate = (r) => {
    // Auth-carrying URL via the existing api client's baseURL + token header
    api.get(`/admin/wallet/induction/${r.id}/mandate`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank", "noopener");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not fetch mandate"));
  };

  return (
    <div className="space-y-4" data-testid="awp-induction-view">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-xl text-[#F5F5F0] flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#D8B23E]" /> Executive Wallet · Induction Queue
            {newToday > 0 && (
              <span data-testid="awp-induction-new-today"
                     className="ml-2 inline-flex items-center gap-1.5 border border-[#C0392B]/60 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-grotesk text-[#F8B5A8]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#C0392B] opacity-70 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#C0392B]" />
                </span>
                {newToday} new · last 24h
              </span>
            )}
          </h3>
          <p className="text-sm text-[#8A8A82] mt-1">
            Applications await your personal review. Authorize to unlock trading power for the partner.
          </p>
        </div>
        <div className="flex gap-2">
          {["pending", "approved", "rejected"].map(s => (
            <button key={s}
                     data-testid={`awp-induction-filter-${s}`}
                     onClick={() => setStatus(s)}
                     className={`px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.24em] ${status === s ? "bg-[#F5F5F0] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></div>
      ) : rows.length === 0 ? (
        <div className="py-14 text-center text-[#8A8A82] font-body text-sm border border-dashed border-white/10">
          No {status} applications right now.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="awp-induction-table">
            <thead>
              <tr className="text-[#8A6A1A] font-grotesk text-[10px] uppercase tracking-[0.28em] border-b border-white/10">
                <th className="text-left py-3">Applicant</th>
                <th className="text-left py-3">Category</th>
                <th className="text-right py-3">Monthly Volume</th>
                <th className="text-right py-3">Annual T/O</th>
                <th className="text-left py-3">Bank Ref</th>
                <th className="text-left py-3">Mandate</th>
                <th className="text-left py-3">Submitted</th>
                <th className="text-right py-3">Action</th>
              </tr>
            </thead>
            <tbody className="text-[#C9C9C2]">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 align-top">
                  <td className="py-3">
                    <div className="text-[#F5F5F0]">{r.user_name || "—"}</div>
                    <div className="text-[#8A8A82] text-xs">{r.user_email}</div>
                    {r.srn && <div className="mt-1 font-mono text-[10px] text-[#D8B23E]">{r.srn}</div>}
                    <div className="mt-2 text-xs text-[#C9C9C2] italic max-w-xs whitespace-normal">"{(r.intention || "").slice(0, 140)}{(r.intention || "").length > 140 ? "…" : ""}"</div>
                  </td>
                  <td className="py-3 text-xs uppercase">{r.primary_category}</td>
                  <td className="py-3 text-right">₹{Number(r.monthly_trade_volume || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 text-right">₹{Number(r.annual_turnover || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 text-xs max-w-[220px] whitespace-normal">{r.bank_reference}</td>
                  <td className="py-3">
                    <button data-testid={`awp-induction-mandate-${r.id}`}
                             onClick={() => openMandate(r)}
                             className="inline-flex items-center gap-1 text-[#D8B23E] hover:text-[#F6E4A6] text-xs font-grotesk uppercase tracking-[0.22em]">
                      <Download className="w-3 h-3" /> PDF
                    </button>
                  </td>
                  <td className="py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td className="py-3 text-right">
                    {status === "pending" ? (
                      <div className="inline-flex gap-1.5">
                        <button data-testid={`awp-induction-authorize-${r.id}`}
                                 disabled={busyId === r.id}
                                 onClick={() => authorize(r)}
                                 className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#D8B23E]/60 text-[#F6E4A6] hover:bg-[#D8B23E] hover:text-black font-grotesk text-[10px] uppercase tracking-[0.24em]">
                          {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          Authorize Wallet
                        </button>
                        <button data-testid={`awp-induction-reject-${r.id}`}
                                 onClick={() => { setRejectFor(r); setRejectReason(""); }}
                                 className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#C0392B]/50 text-[#F8B5A8] hover:bg-[#C0392B] hover:text-white font-grotesk text-[10px] uppercase tracking-[0.24em]">
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className={`font-grotesk text-[10px] uppercase tracking-[0.24em] ${r.status === "approved" ? "text-[#2FB999]" : "text-[#C0392B]"}`}>
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!authorizeFor} onOpenChange={(o) => { if (!o) setAuthorizeFor(null); }}>
        <DialogContent data-testid="awp-induction-authorize-dialog">
          <DialogHeader>
            <DialogTitle>Authorize Executive Wallet · {authorizeFor?.user_email}</DialogTitle>
            <DialogDescription>
              Set optional <b className="text-[#D8B23E]">Executive Conditions</b> that
              will be printed on the Vinci Board Resolution PDF the buyer receives
              upon acknowledgement. Leave blank for unconditional onboarding.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="awp-induction-conditions"
                    value={conditions}
                    onChange={(e) => setConditions(e.target.value)}
                    rows={5}
                    placeholder={'e.g. Subject to monthly audit; Minimum 50 MT per trade; Volume cap ₹1 Cr / month'} />
          <p className="text-[10px] text-[#8A8A82] mt-1 font-body italic">
            One condition per line or separated by semicolons.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuthorizeFor(null)}>Cancel</Button>
            <Button data-testid="awp-induction-authorize-submit"
                    disabled={busyId === authorizeFor?.id}
                    onClick={submitAuthorize}
                    className="bg-[#D8B23E] text-black hover:brightness-110">
              {busyId === authorizeFor?.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {conditions.trim() ? "Authorize with Conditions" : "Authorize Unconditionally"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) setRejectFor(null); }}>
        <DialogContent data-testid="awp-induction-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject wallet induction</DialogTitle>
            <DialogDescription>
              {rejectFor?.user_email} will receive a Vinci-styled rejection email with your note.
              Be candid but kind — this is a serious application.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="awp-induction-reject-reason"
                    value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    rows={4} placeholder="Why are you rejecting? e.g. bank mandate signature missing." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive"
                    data-testid="awp-induction-reject-submit"
                    disabled={rejectReason.trim().length < 6 || busyId === rejectFor?.id}
                    onClick={submitReject}>
              {busyId === rejectFor?.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Send Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
function LiquidityView({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/wallet/liquidity");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load liquidity");
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <div className="p-10 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></div>;
  if (!data) return null;
  const tiles = [
    { label: "Total Ecosystem Balance", value: data.total_ecosystem_balance, colour: "#D8B23E" },
    { label: "Active Trading Power",    value: data.active_trading_power,    colour: "#B87333" },
    { label: "Lifetime Credits",        value: data.total_credited_ever,     colour: "#2FB999" },
    { label: "Lifetime Debits",         value: data.total_debited_ever,      colour: "#C0392B" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <div key={t.label} data-testid={`awp-tile-${t.label.replace(/ /g,'-').toLowerCase()}`}
               className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a]"
               style={{ boxShadow: `0 0 60px -30px ${t.colour}` }}>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em]"
                 style={{ color: t.colour }}>{t.label}</div>
            <div className="font-serif text-3xl mt-2 text-[#F5F5F0]">{inr(t.value)}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 text-[13px] text-[#8A8A82] font-body border border-[#D8B23E]/30 rounded-sm bg-[#1B1401]/40 p-4">
        <ShieldCheck size={14} className="text-[#8A6A1A]" />
        {data.disclaimer}
      </div>
      <div className="flex items-center gap-8 text-[13px] text-[#C9C9C2] font-body">
        <div>Total wallets: <b className="text-[#F5F5F0]">{data.wallet_count}</b></div>
        <div>Active wallets (balance &gt; 0): <b className="text-[#2FB999]">{data.active_wallets}</b></div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
function WalletsView({ api }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [creditTarget, setCreditTarget] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wallet/wallets?q=${encodeURIComponent(q)}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load wallets");
    } finally { setLoading(false); }
  }, [api, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-2.5 text-[#8A8A82]" />
          <Input data-testid="awp-wallets-search"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by email…"
                  className="pl-9 bg-black/40 border-white/20" />
        </div>
        <Button data-testid="awp-wallets-refresh" onClick={load} variant="outline"
                 className="border-white/20 text-[#C9C9C2]">
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
      </div>
      <div className="border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.03] text-[#8A8A82]">
            <tr>
              <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">User</th>
              <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Role</th>
              <th className="text-right px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Balance</th>
              <th className="text-right px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Credited</th>
              <th className="text-right px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Debited</th>
              <th className="text-center px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-[#6f6f68] italic font-body">No wallets found</td></tr>
            ) : rows.map((r) => (
              <tr key={r.user_id} data-testid={`awp-wallet-${r.user_id}`}
                  className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-body text-[#F5F5F0]">{r.user_email}</td>
                <td className="px-4 py-3 text-[#8A8A82] font-body text-[11px] uppercase">{r.role}</td>
                <td className="px-4 py-3 text-right font-mono text-[#D8B23E]">{inr(r.balance)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#2FB999]">{inr(r.total_credited)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#C0392B]">{inr(r.total_debited)}</td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid={`awp-direct-credit-${r.user_email}`}
                      onClick={() => setCreditTarget(r)}
                      title="Push a Vinci Direct Credit to this wallet"
                      className="inline-flex items-center gap-1 text-[9px] font-grotesk uppercase tracking-[0.18em] border border-[#D8B23E]/40 hover:border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 px-2 py-1 rounded-sm">
                      <Zap size={10} /> Direct Credit
                    </button>
                    <InviteButton api={api} email={r.user_email} inducted={r.inducted} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DirectCreditModal open={!!creditTarget} target={creditTarget}
                            setOpen={() => setCreditTarget(null)}
                            api={api} onDone={load} />
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Direct Credit Modal · inline push tool from the Wallets table
   ═════════════════════════════════════════════════════════════════ */
function DirectCreditModal({ open, target, setOpen, api, onDone }) {
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(""); setLabel(""); } }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet/direct-credit", {
        user_email: target.user_email,
        amount: Number(amount),
        label: label.trim(),
      });
      toast.success(`Credited · new balance ${inr(data.balance_after)}`, {
        description: `Notification email dispatched to ${target.user_email}`,
      });
      setOpen();
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Direct credit failed");
    } finally { setBusy(false); }
  };

  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="awp-direct-credit-modal"
                       className="bg-[#0A0906] border border-[#D8B23E]/40 text-[#F5F5F0] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Zap className="text-[#D8B23E]" size={18} /> Vinci Direct Credit
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#C9C9C2] pt-2">
            Push funds into <b className="text-[#F5F5F0]">{target.user_email}</b>'s
            wallet. A gold-on-obsidian "Funds Credited" email fires the
            moment you confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Amount (INR)</Label>
            <Input data-testid="awp-direct-credit-amount" type="number" min={1}
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="bg-black/40 border-white/20 mt-1 text-lg font-mono"
                    placeholder="e.g. 25000" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Label</Label>
            <Input data-testid="awp-direct-credit-label" value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="bg-black/40 border-white/20 mt-1"
                    placeholder="e.g. Quarterly Performance Bonus" />
            <div className="text-[10px] text-[#6f6f68] mt-1 font-body">
              This label lands in the user's ledger + notification email.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={setOpen} disabled={busy}
                   className="border-white/20 text-[#C9C9C2]">Cancel</Button>
          <Button data-testid="awp-direct-credit-submit" onClick={run}
                   disabled={busy || !amount || !label || label.length < 3}
                   className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]">
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Crediting…</>
                   : <><Zap size={12} className="mr-1.5" /> Push Credit</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ═════════════════════════════════════════════════════════════════ */
function RefundsView({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending_admin_review");
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wallet/refund-requests?status=${status}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load refund requests");
    } finally { setLoading(false); }
  }, [api, status]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/wallet/refund-requests/${target.id}/${action}`, { admin_note: note });
      toast.success(`Request ${action}d`);
      setTarget(null); setAction(null); setNote(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {["pending_admin_review", "approved_awaiting_payout", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.18em] rounded-sm border ${
                    status === s ? "border-[#D8B23E] text-[#D8B23E]"
                                    : "border-white/10 text-[#8A8A82] hover:text-[#C9C9C2]"
                  }`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="p-10 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-white/10 rounded-sm p-10 text-center text-[#6f6f68] italic">No refund requests in this lane</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`awp-refund-${r.id}`}
                 className="border border-white/10 rounded-sm p-4 bg-[#0a0a0a]">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-serif text-lg text-[#F5F5F0]">{r.user_email}</div>
                  <div className="text-[11px] text-[#8A8A82] font-body">
                    {r.bank_account_holder} · {r.bank_ifsc} · AC ending {r.bank_account_number?.slice(-4)}
                  </div>
                  <div className="text-[13px] text-[#C9C9C2] mt-2 font-body italic">"{r.reason}"</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-2xl text-[#D8B23E]">{inr(r.amount)}</div>
                  <div className="text-[10px] text-[#6f6f68]">{new Date(r.requested_at).toLocaleString()}</div>
                  <div className="text-[10px] font-grotesk uppercase tracking-[0.22em] mt-1"
                        style={{ color: r.status === "pending_admin_review" ? "#D8B23E"
                                          : r.status === "approved_awaiting_payout" ? "#2FB999" : "#C0392B" }}>
                    {r.status.replace(/_/g, " ")}
                  </div>
                </div>
              </div>
              {r.status === "pending_admin_review" && (
                <div className="mt-3 flex gap-2 justify-end">
                  <Button data-testid={`awp-refund-approve-${r.id}`}
                           onClick={() => { setTarget(r); setAction("approve"); }}
                           className="bg-[#2FB999] hover:bg-[#3FD9B0] text-black font-grotesk text-[10px] uppercase tracking-[0.22em]">
                    <CheckCircle2 size={12} className="mr-1" /> Approve
                  </Button>
                  <Button data-testid={`awp-refund-reject-${r.id}`}
                           onClick={() => { setTarget(r); setAction("reject"); }}
                           variant="outline"
                           className="border-[#C0392B]/50 text-[#C0392B] hover:bg-[#C0392B]/10 font-grotesk text-[10px] uppercase tracking-[0.22em]">
                    <XCircle size={12} className="mr-1" /> Reject
                  </Button>
                </div>
              )}
              {r.admin_note && (
                <div className="mt-2 text-[11px] text-[#8A8A82] font-body italic">Admin note: {r.admin_note}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(v) => { if (!v && !busy) { setTarget(null); setAction(null); setNote(""); } }}>
        <DialogContent data-testid="awp-refund-action-modal"
                         className="bg-[#0A0906] border border-[#D8B23E]/40 text-[#F5F5F0]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {action === "approve" ? "Approve refund" : "Reject refund"} · {target?.user_email}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#C9C9C2] pt-2">
              {action === "approve"
                ? `Debit ${inr(target?.amount || 0)} from user wallet and dispatch via PhonePe Payouts (Razorpay fallback).`
                : `Reject the request. User's wallet balance is untouched.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Admin note (optional)</Label>
            <Textarea data-testid="awp-refund-note" value={note}
                        onChange={(e) => setNote(e.target.value)}
                        className="bg-black/40 border-white/20"
                        placeholder="e.g. Payout scheduled for 12 Feb 2026" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTarget(null); setAction(null); setNote(""); }} disabled={busy}
                     className="border-white/20 text-[#C9C9C2]">Cancel</Button>
            <Button data-testid="awp-refund-confirm" onClick={run} disabled={busy}
                     className={action === "approve"
                       ? "bg-[#2FB999] hover:bg-[#3FD9B0] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]"
                       : "bg-[#C0392B] hover:bg-[#DE4B3C] text-white font-grotesk uppercase tracking-[0.22em] text-[11px]"}>
              {busy ? "Working…" : `Confirm ${action}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
function InviteButton({ api, email, inducted }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet/invite",
        { user_email: email });
      if (data.reused_existing) toast.info("Existing invite re-emailed to " + email);
      else toast.success("Elite Wallet Induction sent to " + email);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={`awp-invite-${email}`}
             onClick={run} disabled={busy}
             title={inducted ? "Already inducted — will re-send invite" : "Send Elite Wallet Induction email"}
             className="inline-flex items-center gap-1 text-[9px] font-grotesk uppercase tracking-[0.18em] border border-[#D8B23E]/40 hover:border-[#D8B23E] hover:text-[#D8B23E] text-[#C9C9C2] px-2 py-1 rounded-sm disabled:opacity-50">
      {busy ? <Loader2 size={10} className="animate-spin" />
             : inducted ? <><CheckCircle2 size={10} className="text-[#2FB999]" /> Re-invite</>
                          : <><Trophy size={10} className="text-[#D8B23E]" /> Invite</>}
    </button>
  );
}

function CreditUserView({ api }) {
  const [form, setForm] = useState({ user_email: "", amount: "",
                                        direction: "credit", reason: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet/manual-adjust",
        { ...form, amount: Number(form.amount) });
      toast.success(`${form.direction} · new balance ${inr(data.balance_after)}`);
      setForm({ user_email: "", amount: "", direction: "credit", reason: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adjustment failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-4 border border-white/10 rounded-sm p-6 bg-[#0a0a0a]">
      <div>
        <div className="font-serif text-xl text-[#F5F5F0]">Manual wallet adjustment</div>
        <div className="text-[13px] text-[#8A8A82] font-body mt-1">
          Credit or debit a user's wallet directly. Every adjustment is HMAC-signed and stamped with your admin ID.
          For the fast-path "Direct Credit" push tool (with a breathtaking user email), use the ⚡ button in the <b className="text-[#D8B23E]">All Wallets</b> tab.
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">User email</Label>
          <Input data-testid="awp-credit-email" value={form.user_email}
                  onChange={(e) => upd("user_email", e.target.value.toLowerCase())}
                  placeholder="buyer@example.com" className="bg-black/40 border-white/20 mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Direction</Label>
            <select data-testid="awp-credit-direction" value={form.direction}
                    onChange={(e) => upd("direction", e.target.value)}
                    className="w-full mt-1 bg-black/40 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0]">
              <option value="credit">Credit (add money)</option>
              <option value="debit">Debit (deduct money)</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Amount (INR)</Label>
            <Input data-testid="awp-credit-amount" type="number" min={1} step="100"
                    value={form.amount} onChange={(e) => upd("amount", e.target.value)}
                    placeholder="e.g. 25000" className="bg-black/40 border-white/20 mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Reason / label</Label>
          <Input data-testid="awp-credit-reason" value={form.reason}
                  onChange={(e) => upd("reason", e.target.value)}
                  placeholder="e.g. August Sales Incentive · Profit Share"
                  className="bg-black/40 border-white/20 mt-1" />
        </div>
      </div>
      <div className="pt-2 border-t border-white/10">
        <Button data-testid="awp-credit-submit" onClick={run}
                 disabled={busy || !form.user_email || !form.amount || !form.reason}
                 className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[11px] w-full">
          {busy ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Applying…</>
                 : <><Plus size={12} className="mr-1.5" /> Apply Adjustment</>}
        </Button>
      </div>
    </div>
  );
}
